import { CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import type { PreflightResult } from '@/types/provision';

interface StepPreflightProps {
  result: PreflightResult | null;
  isDispatching: boolean;
  error: string | null;
  onRun: () => void;
}

function useElapsed(active: boolean): string {
  const [secs, setSecs] = useState(0);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (active) {
      setSecs(0);
      ref.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } else {
      if (ref.current) clearInterval(ref.current);
    }
    return () => {
      if (ref.current) clearInterval(ref.current);
    };
  }, [active]);

  if (!active) return '';
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

export function StepPreflight({ result, isDispatching, error, onRun }: StepPreflightProps): JSX.Element {
  const running = isDispatching || (result !== null && (result.status === 'queued' || result.status === 'running'));
  const elapsed = useElapsed(running);

  const bannerClass = (() => {
    if (!result) return null;
    if (result.status === 'success') return 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-400';
    if (result.status === 'failed') return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-800 dark:text-red-400';
    return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-400';
  })();

  const bannerGlyph = (() => {
    if (!result) return null;
    if (result.status === 'success') return '●';
    if (result.status === 'failed') return '✕';
    return '◐';
  })();

  const bannerLabel = (() => {
    if (!result) return null;
    if (running) return `Running preflight${elapsed ? ` — ${elapsed}` : ''}…`;
    if (result.status === 'success') return 'Preflight complete';
    if (result.status === 'failed') return 'Preflight failed — check IP and SSH credentials';
    return 'Queued';
  })();

  return (
    <div className="space-y-4">
      {/* Status banner */}
      {result && bannerClass && (
        <div className={`rounded-md border px-4 py-3 flex items-center gap-2 text-sm font-medium ${bannerClass}`}>
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : result.status === 'success' ? (
            <CheckCircle2 className="h-4 w-4" aria-hidden />
          ) : (
            <XCircle className="h-4 w-4" aria-hidden />
          )}
          <span>{bannerGlyph} {bannerLabel}</span>
        </div>
      )}

      {/* Run / re-run button */}
      <Button type="button" variant={result ? 'outline' : 'default'} onClick={onRun} disabled={running}>
        {running ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
        ) : result ? (
          <RefreshCw className="h-4 w-4 mr-2" aria-hidden />
        ) : null}
        {result === null ? 'Run preflight' : 'Re-run preflight'}
      </Button>

      {error && (
        <p className="text-sm text-destructive" role="alert">{error}</p>
      )}

      {result?.status === 'success' && result.facts === null && (
        <p className="text-sm text-amber-600">
          Preflight completed but no system facts were reported. Requirements could not be verified.
        </p>
      )}

      {/* Requirements checklist */}
      {result?.requirements && (
        <div className="rounded-md border divide-y">
          {result.requirements.checks.map((check) => (
            <div key={check.key} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              {check.ok ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              ) : (
                <XCircle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
              )}
              <span className="font-medium w-24 shrink-0">{check.label}</span>
              <span className="text-foreground">{check.actual}</span>
              <span className="ml-auto text-muted-foreground text-xs">needs {check.required}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
